import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppConfig } from "../../api/config/config";
import { EndPoints } from "../../Constants/endpoints";
import { useGetData } from "../../hooks";
import { ICountries } from "../../interface/GeneralResponse";
import { setValidationVal } from "../../redux/reducers/phoneValidationSlice";
import { isArabic } from "../../utils/utils";
import Modal from "../Common/Modal";

interface PhoneNumberInputProps {
  controllerName?: string;
  defaultPhone?: string;
  CountryId?: number | null;
  isOptional?: boolean;
  readOnly?: boolean;
  className?: string;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  controllerName,
  defaultPhone,
  CountryId = 1,
  isOptional = false,
  readOnly = false,
  className,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<ICountries | null>(
    null,
  );
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language?.slice(0, 2);
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { data } = useGetData<ICountries[]>(EndPoints.Countries);
  const countriesData = data?.Data ?? [];

  // State to manage focus
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (countriesData && countriesData.length > 0) {
      const preselectedCountry = countriesData.find(
        (country: ICountries) => country.Id === CountryId,
      );
      if (preselectedCountry) {
        setSelectedCountry(preselectedCountry);
        dispatch(
          setValidationVal({
            Length: preselectedCountry.PhoneNumberLength,
            Start: preselectedCountry.PhoneNumberStart,
            Code: preselectedCountry.Code,
            CountryId: preselectedCountry.Id,
          }),
        );
      } else {
        setSelectedCountry(countriesData[0]);
      }
    }
  }, [countriesData, CountryId, dispatch]);

  const handleSelectChange = (option: ICountries) => {
    setSelectedCountry(option);
    setIsModalOpen(false);
    dispatch(
      setValidationVal({
        Length: option.PhoneNumberLength,
        Start: option.PhoneNumberStart,
        Code: option.Code,
        CountryId: option.Id,
      }),
    );
  };

  const error = errors[controllerName || "phone"];
  const errorMessage =
    error && error.message
      ? typeof error.message === "string"
        ? error.message
        : String(error.message)
      : undefined;

  return (
    <div>
      <div
        dir="ltr"
        className={`flex w-full max-w-[95dvw] items-center gap-4 rounded-lg border bg-white ${
          isFocused ? "border-2 border-primary" : "border-systemBg"
        } p-1.5 ${isArabic(i18next?.language) && " "} ${className ?? ""}`}
      >
        {selectedCountry && (
          <button
            type="button"
            className={`flex h-9 items-center gap-1 rounded-[5px] bg-primary-bg px-2 py-2.5`}
            onClick={() => setIsModalOpen(true)}
          >
            <img
              src={`${AppConfig.SERVER_URL}${selectedCountry.Flag}`}
              alt="country-flag"
              className="h-3.5 w-5"
            />
            {selectedCountry.Code}
          </button>
        )}
        <Controller
          name={controllerName || "phone"}
          control={control}
          defaultValue={defaultPhone}
          rules={{
            required: isOptional ? false : t("phone_number_required"),
            validate: (value) => {
              if (isOptional && !value) {
                // If optional and empty, it's valid
                return true;
              }
              const currentCountry = selectedCountry;
              if (!value) {
                return t("phone_number_required");
              }

              const phoneNumber = value;

              if (phoneNumber.startsWith("0")) {
                if (
                  phoneNumber.length !==
                  (currentCountry?.PhoneNumberLength || 0) + 1
                ) {
                  return t("phone_number_must_be_and_start_with", {
                    length: (currentCountry?.PhoneNumberLength || 0) + 1,
                    start: "0",
                  });
                }
              } else if (
                currentCountry &&
                phoneNumber.startsWith(String(currentCountry.PhoneNumberStart))
              ) {
                if (phoneNumber.length !== currentCountry.PhoneNumberLength) {
                  return t("phone_number_must_be_and_start_with", {
                    length: currentCountry.PhoneNumberLength,
                    start: currentCountry.PhoneNumberStart,
                  });
                }
              } else {
                return t("phone_number_must_start_with", {
                  start1: "0",
                  start2: currentCountry?.PhoneNumberStart || "",
                });
              }
              return true;
            },
          }}
          render={({ field }) => (
            <input
              // dir="ltr"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              readOnly={readOnly}
              className={`hide-arrows px-2.5 ${
                currentLanguage === "ar" ? "placeholder:text-right" : ""
              } flex-1 appearance-none border-none !bg-transparent text-primary-text placeholder-body-text placeholder:text-base focus:outline-none`}
              placeholder={t("phone_number")}
              {...field}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={(e) => {
                let value = e.target.value;
                const digitsOnly = value.replace(/\D/g, "");

                // السماح بحد أقصى 10 أرقام
                if (digitsOnly.length > 10) {
                  return;
                }

                // إذا كان هناك أرقام، تأكد من البداية الصحيحة
                if (digitsOnly.length >= 1) {
                  const firstChar = digitsOnly[0];
                  if (firstChar !== "9" && firstChar !== "0") {
                    return; // امنع البدء بأي رقم غير 9 أو 0
                  }
                }

                if (digitsOnly.length >= 2) {
                  if (digitsOnly[0] === "0" && digitsOnly[1] !== "9") {
                    return; // امنع 0 متبوع برقم غير 9
                  }
                }

                // منع 10 أرقام إذا كانت تبدأ بـ 9 (بدون 0)
                if (digitsOnly.length === 10 && digitsOnly[0] === "9") {
                  return; // لا تسمح بـ 10 أرقام عندما تبدأ بـ 9
                }

                field.onChange(digitsOnly);
              }}
              maxLength={10}
            />
          )}
        />
      </div>
      {errorMessage && (
        <p className="mt-1 text-xs text-error">{errorMessage}</p>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        header={t("countries")}
        customStyle="h-96"
      >
        <div className="countries-list overflow-y-auto">
          {countriesData &&
            countriesData.map((country: ICountries) => (
              <button
                key={country.Id}
                type="button"
                onClick={() => handleSelectChange(country)}
                className="flex w-full items-center p-2 hover:bg-primary-bg"
              >
                <img
                  src={`${AppConfig.SERVER_URL}${country.Flag}`}
                  alt={`${country.Name} flag`}
                  className="me-2 w-7"
                />
                {country.Name}
              </button>
            ))}
        </div>
      </Modal>
    </div>
  );
};

export default PhoneNumberInput;
